import { useSyncExternalStore } from 'react'

/**
 * What this browser says about push, held once for the whole tab - `FRONTEND.md` §9.737, corrected after review.
 *
 * **Why one store and not a copy per reader.** Two `usePush` readers are mounted on the page the push offer opens
 * over most: the offer above the router and the account's push row on *Môj profil*, where every sign-in, registration
 * and password reset lands. Each kept its own React copy of the browser's permission and of whether the browser holds
 * a subscription, read once at mount, and a subscribe updated only the copy that made it. So after *Zapnúť
 * upozornenia* in the offer, the row under it went on saying push was off - an unticked switch and *Chcete vedieť,
 * keď je objednávka na ceste?* - until the page reloaded, which reads exactly like the owner's *"still not working
 * properly"*. A refetched server count could not correct it: `subscribedHere` answers *not subscribed* whenever the
 * browser half says it holds nothing. The browser is one browser, so its facts are held once and every reader reads
 * the same value in the same render, through `useSyncExternalStore`.
 *
 * **What is shared, and what is not.**
 * - `permission` and `holds` are the browser's own, whoever is signed in.
 * - `registeredFor` is §9.734's *a subscribe made in this page counts at once* - **for the customer it was made
 *   for**. Signing out and in as somebody else in the same tab does not make it theirs: the subscription was saved to
 *   the first account, so the second is asked (the review found the offer, which stays mounted for the tab's life,
 *   carrying it across accounts).
 *
 * **Asked once, by whoever reads it first.** The probe - does the browser already hold a subscription - runs when the
 * first reader subscribes, and a subscribe or an unsubscribe made in this tab answers it from then on: an older probe
 * that lands afterwards is discarded rather than overwriting the newer answer.
 *
 * **Forgotten when nobody reads it.** In the app the offer is mounted for the life of the page, so this never
 * happens; when the last reader does go, the next one asks the browser afresh rather than trusting a copy nobody was
 * keeping current.
 */
export interface PushBrowser {
  /** `Notification`, a service worker and `PushManager` all exist. Does not change under the page. */
  supported: boolean
  /** The permission, as this page last read it or last changed it. */
  permission: NotificationPermission
  /** Whether this browser holds a subscription - `null` until the probe has answered either way. */
  holds: boolean | null
  /** The customer a subscribe made in this page was saved for, or `null`. */
  registeredFor: number | null
}

/** What a server render, which never happens here, would read: nothing to offer. */
const NO_BROWSER: PushBrowser = { supported: false, permission: 'denied', holds: null, registeredFor: null }

let current: PushBrowser | null = null
let probeStarted = false
/** Bumped by every probe and every answer made in this tab, so only the newest word about the subscription lands. */
let latest = 0
const listeners = new Set<() => void>()

function browserSupportsPush(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  )
}

function fresh(): PushBrowser {
  const supported = browserSupportsPush()
  return {
    supported,
    permission: supported ? Notification.permission : 'denied',
    holds: null,
    registeredFor: null,
  }
}

/** The browser's facts as every reader in the tab sees them. Read lazily: the first reader's render asks. */
export function readPushBrowser(): PushBrowser {
  current ??= fresh()
  return current
}

function update(next: Partial<PushBrowser>): void {
  current = { ...readPushBrowser(), ...next }
  for (const listener of listeners) listener()
}

/** Whether this browser already holds a subscription - asynchronous, so it cannot be asked during a render. */
function probe(): void {
  if (probeStarted || !readPushBrowser().supported) return
  probeStarted = true
  latest += 1
  const ticket = latest
  const answer = (holds: boolean) => {
    if (ticket === latest) update({ holds })
  }
  try {
    void navigator.serviceWorker.ready
      .then((registration) => registration.pushManager.getSubscription())
      .then(
        (subscription) => answer(subscription !== null),
        () => answer(false),
      )
  } catch {
    answer(false)
  }
}

export function subscribePushBrowser(onChange: () => void): () => void {
  listeners.add(onChange)
  probe()
  return () => {
    listeners.delete(onChange)
    if (listeners.size === 0) {
      current = null
      probeStarted = false
      // A probe still in flight belongs to the copy just forgotten.
      latest += 1
    }
  }
}

/** The browser answered the permission prompt - whatever it answered, every reader sees it. */
export function notePushPermission(permission: NotificationPermission): void {
  update({ permission })
}

/** A subscribe made in this tab, saved for `customerId`. */
export function notePushSubscribed(customerId: number | null): void {
  latest += 1
  update({ permission: 'granted', holds: true, registeredFor: customerId })
}

/** An unsubscribe made in this tab: the browser holds nothing, and nobody's subscribe here counts any more. */
export function notePushUnsubscribed(): void {
  latest += 1
  update({ holds: false, registeredFor: null })
}

/** {@link readPushBrowser}, as a value every reader in the tab agrees on in the same render. */
export function usePushBrowser(): PushBrowser {
  return useSyncExternalStore(subscribePushBrowser, readPushBrowser, () => NO_BROWSER)
}
